/** Picking — the store / warehouse order queue (New → Processing → Picking → Packing → Ready) with the pick list in
 * aisle order, missing items and customer-approved substitutions. Used by tenant admins (Admin → Location
 * Service) and shop owners (Merchant → Picking); the server limits owners to their own shops' orders. */
import { useEffect, useState } from "react";
import { ArrowLeft, Check, CheckCircle2, Loader2, PackageX, Replace, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { inr, useLse } from "../../../lib/lse";
import { Btn, Empty, ErrorBox, Loading, Modal, PageHead, Pill } from "./LseUI";

const FLOW = ["NEW", "PROCESSING", "PICKING", "PACKING", "READY"];
const NEXT_LABEL = { NEW: "Start processing", PROCESSING: "Start picking", PICKING: "Start packing", PACKING: "Mark ready" };
const STATE_TONE = { PENDING: "muted", PICKED: "green", PARTIAL: "amber", UNAVAILABLE: "red", SUBSTITUTED: "blue", REMOVED: "red", PARTIAL_CLOSED: "amber" };
const errText = (e) => e?.response?.data?.detail || "Something went wrong";

export default function LsePicking() {
    const [status, setStatus] = useState("");
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [open, setOpen] = useState(null);
    // /merchant/picking is a plain route: without the module the page shows a notice and calls nothing (the menu
    // link is already hidden, and the server refuses the queue anyway).
    const on = useLse().has("warehouse_picking");
    const load = () => {
        if (!on) return;
        setErr(null);
        api.get(`/lse/picking/queue${status ? `?status=${status}` : ""}`).then(({ data: d }) => setData(d)).catch((e) => setErr(errText(e)));
    };
    useEffect(load, [status, on]);
    useEffect(() => {
        if (!on) return undefined;
        const h = setInterval(load, 30000);
        return () => clearInterval(h);
    }, [status, on]);   // eslint-disable-line react-hooks/exhaustive-deps

    if (!on) return <div data-testid="lse-picking-off" className="p-6 text-center text-sm text-muted-foreground">Picking isn&apos;t enabled for this store.</div>;
    if (open) return <PickList orderId={open} onBack={() => { setOpen(null); load(); }} />;
    return (
        <div data-testid="lse-picking">
            <PageHead title="Picking" sub="Orders to pick and pack, oldest first. Items are listed in aisle / bin order." />
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                {[["", "Open"], ...FLOW.map((s) => [s, s.charAt(0) + s.slice(1).toLowerCase()])].map(([k, label]) => (
                    <button key={k || "open"} type="button" onClick={() => setStatus(k)} data-testid={`lse-pick-filter-${k || "open"}`}
                            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${status === k ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                        {label}{k && data?.counts?.[k] != null ? ` · ${data.counts[k]}` : ""}
                    </button>
                ))}
            </div>
            {err && <ErrorBox message={err} onRetry={load} />}
            {!data && !err && <Loading />}
            {data?.pick_lists?.length === 0 && <Empty>Nothing to pick right now.</Empty>}
            <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {(data?.pick_lists || []).map((p) => (
                    <button key={p.id} type="button" onClick={() => setOpen(p.order_id)} data-testid={`lse-pick-${p.order_code}`}
                            className="text-left bg-card border border-border rounded-2xl p-4 hover:border-primary">
                        <div className="flex items-center justify-between gap-2">
                            <p className="font-bold">{p.order_code}</p>
                            <Pill tone={p.status === "READY" ? "green" : p.status === "NEW" ? "amber" : "blue"}>{p.status.toLowerCase()}</Pill>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{p.lines.length} item(s) · {p.order?.customer_name || "Customer"}</p>
                        {p.order?.delivery_plan && <p className="text-xs mt-1 font-semibold">{p.order.delivery_plan.label}{p.order.delivery_plan.date_label ? ` · ${p.order.delivery_plan.date_label}` : ""}{p.order.delivery_plan.slot?.label ? ` · ${p.order.delivery_plan.slot.label}` : ""}</p>}
                        {p.blocking > 0 && p.status !== "NEW" && <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">{p.blocking} item(s) to settle</p>}
                    </button>
                ))}
            </div>
        </div>
    );
}

function PickList({ orderId, onBack }) {
    const [pl, setPl] = useState(null);
    const [busy, setBusy] = useState(null);
    const [subFor, setSubFor] = useState(null);
    const load = () => api.get(`/lse/picking/${orderId}`).then(({ data }) => setPl(data)).catch((e) => toast.error(errText(e)));
    useEffect(() => { load(); }, [orderId]);   // eslint-disable-line react-hooks/exhaustive-deps
    if (!pl) return <Loading />;
    const act = async (key, fn) => {
        setBusy(key);
        try { const { data } = await fn(); setPl((p) => ({ ...p, ...data })); if (data?.warning) toast.warning(data.warning); }
        catch (e) { toast.error(errText(e)); } finally { setBusy(null); load(); }
    };
    const idx = FLOW.indexOf(pl.status);
    const next = FLOW[idx + 1];
    const lines = [...pl.lines].sort((a, b) => a.pick_seq - b.pick_seq);
    const canPick = ["PROCESSING", "PICKING"].includes(pl.status);

    return (
        <div data-testid="lse-picklist">
            <button type="button" onClick={onBack} className="text-sm font-semibold inline-flex items-center gap-1 mb-3"><ArrowLeft size={16} /> Queue</button>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-xl font-bold">{pl.order_code}</h2>
                    <p className="text-sm text-muted-foreground">{pl.order?.customer_name} · {pl.status.toLowerCase()}{pl.order?.delivery_plan ? ` · ${pl.order.delivery_plan.label} ${pl.order.delivery_plan.date_label || ""} ${pl.order.delivery_plan.slot?.label || ""}` : ""}</p>
                    {pl.order?.notes && <p className="text-sm mt-1">Note: {pl.order.notes}</p>}
                </div>
                {next && <Btn onClick={() => act("adv", () => api.post(`/lse/picking/${orderId}/advance`, { to: next }))} disabled={!!busy || (["PACKING", "READY"].includes(next) && pl.blocking > 0)} data-testid="lse-pick-advance">
                    {busy === "adv" ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} {NEXT_LABEL[pl.status]}
                </Btn>}
            </div>
            <ol className="mt-4 space-y-2">
                {lines.map((ln) => (
                    <li key={ln.line_no} className="bg-card border border-border rounded-2xl p-3 flex gap-3 items-start" data-testid={`lse-pick-line-${ln.line_no}`}>
                        <span className="w-8 h-8 rounded-full bg-muted font-bold text-sm flex items-center justify-center shrink-0">{ln.pick_seq}</span>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">{ln.name}{ln.variant_label ? ` · ${ln.variant_label}` : ""} <span className="text-muted-foreground">× {ln.qty}</span></p>
                            <p className="text-xs text-muted-foreground">{ln.bin_location ? `Bin ${ln.bin_location}` : "No bin"}{ln.category ? ` · ${ln.category}` : ""} · {inr(ln.unit_price)} each</p>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <Pill tone={STATE_TONE[ln.state]}>{ln.state.replace(/_/g, " ").toLowerCase()}{ln.state === "PARTIAL" ? ` ${ln.picked_qty}/${ln.qty}` : ""}</Pill>
                                {ln.substitution && <Pill tone={ln.substitution.status === "APPROVED" ? "green" : ln.substitution.status === "REJECTED" ? "red" : "amber"}>sub: {ln.substitution.name} · {ln.substitution.status.toLowerCase()}</Pill>}
                            </div>
                        </div>
                        {canPick && (
                            <div className="flex flex-col gap-1.5 shrink-0">
                                {ln.state !== "PICKED" && !ln.substitution && (
                                    <Btn className="!px-3 !min-h-[36px]" onClick={() => act(`p${ln.line_no}`, () => api.patch(`/lse/picking/${orderId}/lines/${ln.line_no}`, {}))} data-testid={`lse-pick-ok-${ln.line_no}`}><Check size={14} /> Picked</Btn>
                                )}
                                {["PENDING", "PICKED"].includes(ln.state) && (
                                    <Btn tone="ghost" className="!px-3 !min-h-[36px]" onClick={() => act(`u${ln.line_no}`, () => api.patch(`/lse/picking/${orderId}/lines/${ln.line_no}`, { unavailable: true }))} data-testid={`lse-pick-missing-${ln.line_no}`}><PackageX size={14} /> Missing</Btn>
                                )}
                                {["UNAVAILABLE", "PARTIAL"].includes(ln.state) && !ln.substitution && (
                                    <>
                                        <Btn tone="ghost" className="!px-3 !min-h-[36px]" onClick={() => setSubFor(ln)} data-testid={`lse-pick-sub-${ln.line_no}`}><Replace size={14} /> Substitute</Btn>
                                        <Btn tone="ghost" className="!px-3 !min-h-[36px]" onClick={() => act(`c${ln.line_no}`, () => api.post(`/lse/picking/${orderId}/lines/${ln.line_no}/close`))}><X size={14} /> Remove item</Btn>
                                    </>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ol>
            {(pl.pending_adjustments || []).length > 0 && (
                <p className="mt-3 text-xs text-muted-foreground">Cash on delivery not yet collected — adjust the amount collected by {inr(pl.pending_adjustments.reduce((s, a) => s + a.amount, 0))} for removed / cheaper items.</p>
            )}
            {subFor && <SubstitutePicker orderId={orderId} line={subFor} onClose={() => setSubFor(null)} onDone={() => { setSubFor(null); load(); }} />}
        </div>
    );
}

function SubstitutePicker({ orderId, line, onClose, onDone }) {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState(null);
    useEffect(() => {
        const h = setTimeout(() => {
            api.get(`/lse/picking/${orderId}/lines/${line.line_no}/substitutes${q ? `?q=${encodeURIComponent(q)}` : ""}`)
                .then(({ data }) => setRows(data.candidates || [])).catch(() => setRows([]));
        }, 250);
        return () => clearTimeout(h);
    }, [q, orderId, line.line_no]);
    const propose = async (p) => {
        try { await api.post(`/lse/picking/${orderId}/lines/${line.line_no}/substitute`, { product_id: p.id }); toast.success("Sent to the customer for approval"); onDone(); }
        catch (e) { toast.error(errText(e)); }
    };
    return (
        <Modal title={`Substitute for ${line.name}`} onClose={onClose}>
            <p className="text-xs text-muted-foreground">Only items at or below {inr(line.unit_price)} are listed — the customer never pays more. They approve or reject in their order screen.</p>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="mt-3 w-full rounded-xl bg-muted px-3 py-2.5 text-sm" />
            {!rows ? <Loading /> : rows.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No suitable items.</p> : (
                <ul className="mt-3 divide-y divide-border">
                    {rows.map((p) => (
                        <li key={p.id} className="py-2 flex items-center gap-3">
                            <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{p.name}</p><p className="text-xs text-muted-foreground">{inr(p.price)}{p.same_category ? " · same category" : ""}{p.sellable != null ? ` · ${p.sellable} in stock` : ""}</p></div>
                            <Btn className="!px-3 !min-h-[36px]" onClick={() => propose(p)}>Propose</Btn>
                        </li>
                    ))}
                </ul>
            )}
        </Modal>
    );
}
